import { useCallback, useMemo, useRef } from 'react'
import { useHref, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useOnChange, useRemembered } from './listMemory'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Search } from 'lucide-react'
import { SkeletonRows } from './Skeleton'
import { DeniedState, EmptyState, ErrorState } from './States'
import { table as S } from './strings'
import './DataTable.css'

/**
 * The list surface every admin page is built on — a faithful clone of the legacy
 * DataTables 1.13 setup, minus the jQuery.
 *
 * Behaviour kept from `_AdminsBaseLayout.cshtml:1152-1310`:
 *   page sizes 10 / 25 / 50 / 100 (default 10), initial sort on the first column
 *   ascending, `simple_numbers` paging, and the Arabic language pack verbatim.
 *
 * Columns:
 *   { key, header, render?, value?, sortable?, searchable?, align?, width? }
 *   `value` feeds sorting and search; `render` draws the cell. Both default to
 *   `row[key]`. A cell with no `render` is one line, cut with an ellipsis and the whole
 *   value in its tooltip, so one long value cannot make its row twice as tall.
 *
 * NO ROW NUMBERS by default: a column keyed `index` is dropped unless `showIndex` —
 * "row 7 of this page" names nothing a reader can use, and it spends a column's width.
 * A column keyed `actions` is the row's actions, always drawn as tinted chips.
 *
 * A ROW WITH DETAILS OPENS FROM ANYWHERE ON IT (2026-10-19, the client's request):
 * `rowTo(row)` names its details page, `onRowClick(row)` opens details that live in a
 * sheet. The name link and the details action stay — they are the keyboard's way in.
 *
 * A page that puts a `FilterBar` above the list owns the search box itself and
 * passes `query` down; the table then drops its own controls row and is only rows,
 * a sticky header and the pager.
 */

/* One frozen empty list, shared. A fresh `[]` per render would be a new reference every
   time and would invalidate every memo below that depends on the rows. */
const NO_ROWS = Object.freeze([])

const PAGE_SIZES = [10, 25, 50, 100]

const cellValue = (column, row) => (column.value ? column.value(row) : row[column.key])

/**
 * What a column SORTS on, when that differs from what it shows and searches.
 *
 * Needed wherever the API hands over a pre-formatted date. `"13-08-2025 09:41 AM"` compared as
 * text sorts by day of month, so a column can look sorted and be wrong — and switching `value` to
 * a timestamp instead would take the date out of the search index. `sortValue` separates the two:
 * `value` stays the readable text, `sortValue` returns something that orders correctly.
 */
const sortKey = (column, row) => (column.sortValue ? column.sortValue(row) : cellValue(column, row))

const compare = (a, b) => {
  // Both-null must return 0, or the comparator is not a total order and V8 shuffles
  // the null run differently on every click.
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'ar')
}

const isActions = (column) => column.key === 'actions'

/* What a click on a row leaves to its own target: every control a cell can hold. */
const ROW_CONTROLS =
  'a, button, input, select, textarea, label, summary, [contenteditable="true"], ' +
  '[role="button"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="tab"], ' +
  '[data-row-ignore]'

/**
 * Whether a click landed on the ROW rather than on something in it. React bubbles events
 * from portals through their React parents, so a menu or a dialog opened from a cell
 * reaches the row's handler although it is drawn elsewhere on the page — hence the
 * containment test as well as the control test.
 */
const onRowItself = (event) => {
  const row = event.currentTarget
  if (!row.contains(event.target)) return false
  const control = event.target.closest(ROW_CONTROLS)
  return !control || !row.contains(control)
}

/** A plain value on one line; the tooltip carries what the ellipsis cut. */
function TextCell({ value }) {
  if (value == null || value === '') return null
  if (typeof value !== 'string' && typeof value !== 'number') return value
  return (
    <span className="data-table__text" title={String(value)}>
      {value}
    </span>
  )
}

/** The `simple_numbers` window: first, last, and a run around the current page. */
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages = new Set([1, total, current])
  for (let offset = -1; offset <= 1; offset += 1) {
    const page = current + offset
    if (page > 1 && page < total) pages.add(page)
  }

  const sorted = [...pages].sort((a, b) => a - b)
  return sorted.flatMap((page, index) =>
    index > 0 && page - sorted[index - 1] > 1 ? ['gap', page] : [page],
  )
}

export default function DataTable({
  columns,
  /* A LIST THAT IS NOT A LIST IS AN EMPTY ONE, never a thrown render. Forty-odd screens
     pass this, several of them mid-load or straight off a response that answered with a
     shape nobody expected — and `[...rows]` on an `undefined` does not fail the table, it
     unmounts the whole app to a white page. Normalised once, here, so a caller's bad
     moment costs its own empty state instead of the router. */
  rows: rowsProp,
  getRowId = (row, index) => row.id ?? index,
  title,
  icon: Icon,
  toolbar,
  footer,
  loading = false,
  emptyMessage = S.zeroRecords,
  /* The load FAILED. Distinct from "no rows", and the distinction is the whole point: a
     screen that renders its empty state for a failure tells the operator there is nothing
     there, which is the opposite of the truth. Every list endpoint in this product can
     answer a database fault as HTTP 200 with an `Error` field, so this is not an edge case. */
  error = null,
  initialSort,
  /* Details that open in place — a sheet, a drawer. */
  onRowClick,
  /**
   * THE ROW'S DETAILS PAGE, as a path — or null for a row that has none. The whole row
   * then opens it the way its name link does: a plain click navigates; Ctrl, ⌘, Shift or
   * the middle button open a new tab. A row that only answered the plain click would be a
   * link that forgot half of what links do.
   */
  rowTo,
  isRowSelected,
  /* `paging: false` in DataTables — every row shows at once and the length select
     and pager go away, leaving search and the row count. */
  paginate = true,
  /* Some legacy tables hide their own `_filter` box and drive filtering from a
     toolbar menu instead. */
  searchable = true,
  /* Controlled search text, when a FilterBar above the list owns the field. */
  query: externalQuery,
  /* `false` drops the table's own search box. The page size is in the footer with the
     pager and follows `paginate` alone. */
  controls = true,
  /**
   * SERVER-SIDE MODE (W1). Omit it and the table behaves exactly as before: it filters,
   * sorts and pages the `rows` it was handed.
   *
   * Pass `{ total, page, pageSize, sort, onChange }` and it stops doing any of that —
   * `rows` IS the page, `total` drives the pager, and every control reports upwards
   * instead of acting locally. Lists in this product are server-paged because a table of
   * users or a phishing report is far too large to send at once; the difference has to
   * live here rather than in every screen.
   *
   * `onChange({ page, pageSize, sort, query })` receives the whole next state, so a
   * caller can put it straight into one request.
   */
  server = null,
  /**
   * WHAT ELSE NARROWS THIS LIST. A scope card, a category select, a set of checkboxes — a
   * filter the SCREEN owns rather than the table. The table cannot see those, so page 5 of
   * a filtered list stayed page 5 when the filter changed and the list became two pages
   * long; the render clamped it and the address went on claiming otherwise.
   *
   * Pass anything that changes when the row set does — a string, a number, `JSON.stringify`
   * of the filter object — and choosing a new filter starts at page 1 again.
   */
  resetKey = null,
  /**
   * WHERE THIS LIST WAS LEFT IS REMEMBERED BY DEFAULT — the page, the page size and the
   * sort come back when the screen is reopened, so opening a record and pressing Back
   * lands on the page it was opened from rather than on page 1.
   *
   * The key is the route plus this table's own columns, which is stable across mounts of
   * the same table and different for a second table on the same screen. Pass `stateKey`
   * to name it explicitly, or `stateKey={false}` for a table whose position should not
   * survive — a picker inside a sheet, a preview.
   *
   * Only for tables the table itself pages; a SERVER-paged list holds that state in its
   * own screen, and remembers it there with the same `useRemembered`.
   */
  stateKey,
  /**
   * THIS TABLE'S PAGE LIVES IN THE URL, as `?page=4`.
   *
   * The remembering above is for the tab that is reading; this is for the ADDRESS, and it
   * is the half that always works: the history entry the browser goes back to already
   * carries the page, so returning from a record replays nothing, a refresh keeps the
   * view, and the position can be sent to someone else. Written with `replace`, so paging
   * through a list is one history entry rather than four.
   *
   * Pass another NAME where a screen shows two lists at once — they cannot share one
   * parameter — or `null` to keep a table out of the address entirely. `stateKey={false}`
   * already implies it: a list inside a sheet is neither remembered nor addressed.
   *
   * Server-paged screens do the same thing with `useListParams`, since they own the page.
   */
  pageParam = 'page',
  /* Opt back in to the row-number column — see the note at the top. */
  showIndex = false,
}) {
  const rows = Array.isArray(rowsProp) ? rowsProp : NO_ROWS
  const shown = useMemo(
    () => (showIndex ? columns : columns.filter((column) => column.key !== 'index')),
    [columns, showIndex],
  )

  const { pathname, hash } = useLocation()
  const navigate = useNavigate()
  /* The router's base, so a row opened in a new tab gets a real address. */
  const base = useHref('/').replace(/\/$/, '')
  /* Where the pointer went down on a row: a release more than a few pixels away is the
     end of a drag — selecting, or just a hand moving — not a click on the row. */
  const pressAt = useRef(null)
  const wasDrag = (event) => {
    const press = pressAt.current
    pressAt.current = null
    return Boolean(press) && Math.hypot(event.clientX - press[0], event.clientY - press[1]) > 4
  }
  /* Two tables on one screen differ by their columns; the same table across two mounts
     does not. `false` opts out entirely. */
  const memoryKey = useMemo(() => {
    if (stateKey === false) return null
    if (stateKey) return `table:${stateKey}`
    return `table:${pathname}#${columns.map((column) => column.key).join(',')}`
  }, [stateKey, pathname, columns])
  /* THE BUILT-IN SEARCH BOX IS REMEMBERED TOO. A table that is not handed a `query` keeps
     its own, and it used to be plain state: the page came back and the text that narrowed
     it did not, so you returned to page 3 of a list you were no longer searching. */
  const [ownQuery, setOwnQuery] = useRemembered(memoryKey && `${memoryKey}:query`, '')
  const query = externalQuery ?? ownQuery
  const [ownSort, setSort] = useRemembered(
    memoryKey && `${memoryKey}:sort`,
    initialSort ?? { key: columns.find((column) => column.sortable !== false)?.key, dir: 'asc' },
  )
  /* A server-paged list that hands over `server.sort` owns the order the way it owns the
     page: the arrow shows what the SCREEN asked the server for, and the two cannot drift.
     Without it the table kept a private copy that opened on "first column, ascending"
     whatever order the rows actually arrived in. */
  const sort = server && server.sort !== undefined ? server.sort : ownSort
  const [ownPageSize, setOwnPageSize] = useRemembered(
    memoryKey && `${memoryKey}:pageSize`,
    PAGE_SIZES[0],
  )
  const [rememberedPage, setRememberedPage] = useRemembered(memoryKey && `${memoryKey}:page`, 1)

  /* The address owns the page unless this table has opted out of both. */
  const urlKey = stateKey === false ? null : pageParam
  const [params] = useSearchParams()
  const urlPage = urlKey ? Number(params.get(urlKey)) : 0
  const ownPage = urlKey ? (Number.isFinite(urlPage) && urlPage > 0 ? urlPage : 1) : rememberedPage
  const setOwnPage = useCallback(
    (nextPage) => {
      if (!urlKey) {
        setRememberedPage(nextPage)
        return
      }
      const next = new URLSearchParams(params)
      if (nextPage > 1) next.set(urlKey, String(nextPage))
      else next.delete(urlKey)
      const search = next.toString()
      /* Through `navigate` rather than `setSearchParams`, which would drop the `#section`
         a tabbed screen lives in — see components/useListParams.js. */
      navigate({ search: search ? `?${search}` : '', hash }, { replace: true })
    },
    [urlKey, params, hash, navigate, setRememberedPage],
  )

  // In server mode the caller owns these; locally they are the table's own.
  const pageSize = server ? (server.pageSize ?? PAGE_SIZES[0]) : ownPageSize
  const page = server ? (server.page ?? 1) : ownPage

  // Searching or resizing the page jumps back to page 1; a changed `rows` does not,
  // which mirrors the legacy `ajax.reload(null, false)` used after inline edits.
  // Out-of-range pages are clamped below instead.
  /* THROUGH `useOnChange`, NOT A `firstRun` REF. The ref survives StrictMode's
     mount-unmount-mount, so the second pass read as a change and reset the page the
     screen had just restored from the address: a report opened from page 3 of the
     security reports came back to page 1, which is the whole thing a remembered
     position exists to prevent. `useOnChange` compares the dependencies rather than
     counting renders, which is what "when this changes" actually means. */
  useOnChange(() => {
    if (!server) setOwnPage(1)
    /* `setOwnPage` is deliberately NOT a dependency. It closes over react-router's own
       setter, whose identity changes with every navigation — including the one this
       effect's own write causes — so listing it makes paging a loop that lands back on
       page 1 the moment a page is chosen. What this effect reacts to is a new search, a
       new page size or a new filter outside the table, and that is what it lists. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, ownPageSize, server, resetKey])

  /** One place that decides whether a control acts locally or reports upwards. */
  const apply = (next) => {
    if (!server) {
      if (next.page !== undefined) setOwnPage(next.page)
      if (next.pageSize !== undefined) setOwnPageSize(next.pageSize)
      if (next.sort !== undefined) setSort(next.sort)
      return
    }

    /*
     * THE SORT IS KEPT HERE TOO, even though the server owns the rows.
     *
     * `sort` is what draws the column's arrow and what `toggleSort` reads to decide the
     * next direction. In server mode it was reported upward and never stored, so it stayed
     * on its initial `asc` for ever: the arrow never moved, and every click computed
     * `asc -> desc` from the stale value, which made DESCENDING the only order a
     * server-paged list could ever be in. The rows still come from the server; this is the
     * table remembering what it asked for.
     */
    if (next.sort !== undefined) setSort(next.sort)
    server.onChange({ page, pageSize, sort, query, ...next })
  }

  const filtered = useMemo(() => {
    // The server already searched; re-filtering here would hide rows it deliberately sent.
    if (server) return rows
    const needle = query.trim().toLowerCase()
    if (!needle) return rows
    const searchable = columns.filter((column) => column.searchable !== false)
    return rows.filter((row) =>
      searchable.some((column) => String(cellValue(column, row) ?? '').toLowerCase().includes(needle)),
    )
  }, [rows, columns, query, server])

  const sorted = useMemo(() => {
    // Likewise the ordering: sorting a single page would reorder within it only.
    if (server) return filtered
    const column = columns.find((item) => item.key === sort?.key)
    if (!column) return filtered
    const direction = sort.dir === 'desc' ? -1 : 1
    return [...filtered].sort((a, b) => compare(sortKey(column, a), sortKey(column, b)) * direction)
  }, [filtered, columns, sort, server])

  /* `total` is what the pager divides — the count the SERVER matched, not the length of
     the page it sent. */
  const totalRows = server ? (server.total ?? rows.length) : sorted.length
  const pageCount = paginate ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1
  /* Clamp only against a list that has arrived. A remembered page 8 rendered while the
     rows are still in flight would otherwise show as page 1 for as long as the request
     takes — and read as "Back forgot where I was" even though it did not. Once it HAS
     arrived the clamp applies, empty included: a list of nothing is one page, and leaving
     the pager on page 8 there left Next enabled over a phantom. */
  const currentPage = loading ? page : Math.min(page, pageCount)
  const start = paginate ? (currentPage - 1) * pageSize : 0
  const visible = server || !paginate ? sorted : sorted.slice(start, start + pageSize)

  const toggleSort = (column) => {
    if (column.sortable === false) return
    const next =
      sort?.key === column.key
        ? { key: column.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key: column.key, dir: 'asc' }

    // Re-sorting means a different first page, so it returns to it.
    apply({ sort: next, page: 1 })
  }

  return (
    <section className="data-table" data-testid="table">
      {/* ONE BAR, NOT TWO ROWS. The title/toolbar and the length/search sat in
          separate full-width rows carrying their own padding — 132px of chrome
          above the first cell for three small controls, which on a 768px laptop
          was a fifth of the screen spent before any data. They are flex items of
          one bar now: side by side while the column can hold both, wrapping to
          the old two lines when it cannot. */}
      {/* The page size lives in the FOOTER beside the pager it sizes, so the bar is only
          a title, a toolbar and — where no FilterBar owns it — the search. */}
      {(title || toolbar || (controls && searchable && externalQuery === undefined)) && (
        <div className="data-table__bar">
        {(title || toolbar) && (
          <header className="data-table__header">
            {title && (
              <h3 className="data-table__title">
                {Icon && <Icon size={19} strokeWidth={1.75} />}
                <bdi>{title}</bdi>
              </h3>
            )}
            {toolbar && <div className="data-table__toolbar">{toolbar}</div>}
          </header>
        )}

        {controls && searchable && externalQuery === undefined && (
        <div className="data-table__controls">
          {/* The label rides inside the field as its placeholder rather than
              standing outside it with a colon — a DataTables habit that reads as
              a form row, next to the FilterBar the other lists use. The colon
              goes with it; the accessible name keeps the legacy string. */}
          <span className="data-table__search">
            <span className="data-table__search-field">
              <Search size={16} strokeWidth={1.75} aria-hidden="true" />
              <input
                type="search"
                value={ownQuery}
                onChange={(event) => setOwnQuery(event.target.value)}
                placeholder={S.search.replace(/:$/, '')}
                aria-label={S.search.replace(/:$/, '')}
                autoComplete="off"
                data-testid="table-search"
              />
            </span>
          </span>
        </div>
        )}
        </div>
      )}

      <div className="data-table__scroll">
        <table className="data-table__table">
          <thead>
            <tr>
              {shown.map((column) => {
                const active = sort?.key === column.key
                const sortable = column.sortable !== false
                return (
                  <th
                    key={column.key}
                    /* `width` alone is advisory — a wide table squeezes columns
                       past it and clips their content. Pinning minWidth too makes
                       the table overflow and scroll instead. */
                    style={
                      column.width
                        ? { width: column.width, minWidth: column.width }
                        : undefined
                    }
                    className={`data-table__th data-table__cell--${column.align ?? 'start'}${
                      sortable ? ' is-sortable' : ''
                    }${active ? ' is-sorted' : ''}${isActions(column) ? ' data-table__cell--actions' : ''}`}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    /* One line, cut — the full name in the tooltip. */
                    title={typeof column.header === 'string' ? column.header : undefined}
                  >
                    {/* A real button, so sorting is reachable by keyboard. */}
                    {sortable ? (
                      <button
                        type="button"
                        className="data-table__th-inner"
                        onClick={() => toggleSort(column)}
                      >
                        <bdi>{column.header}</bdi>
                        {active ? (
                          sort.dir === 'asc' ? (
                            <ArrowUp size={13} strokeWidth={2.25} />
                          ) : (
                            <ArrowDown size={13} strokeWidth={2.25} />
                          )
                        ) : (
                          <ChevronsUpDown
                            className="data-table__sort-idle"
                            size={13}
                            strokeWidth={2}
                          />
                        )}
                      </button>
                    ) : (
                      <span className="data-table__th-inner">
                        <bdi>{column.header}</bdi>
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          {/* Placeholder rows rather than a spinner: the table keeps its shape,
              so the data arriving is a fill instead of the page jumping from an
              empty panel to a full one. */}
          {loading && <SkeletonRows columns={shown.length} />}

          {!loading && visible.length > 0 && (
            <tbody data-testid="table-body">
              {visible.map((row, index) => {
                const selected = isRowSelected?.(row) ?? false
                const to = rowTo?.(row) ?? null
                const opens = Boolean(to || onRowClick)
                return (
                <tr
                  key={getRowId(row, start + index)}
                  data-testid="table-row"
                  data-row-key={getRowId(row, start + index)}
                  className={`${opens ? 'is-clickable' : ''}${selected ? ' is-selected' : ''}`.trim() || undefined}
                  aria-selected={isRowSelected ? selected : undefined}
                  /* A row action is not the row. Every list here puts buttons and links in
                     its last column, and a click on one bubbled up to the row as well — so a
                     row that opens a record opened it TWICE, leaving two identical history
                     entries and a Back that appeared to do nothing. The end of a drag, or of
                     one that selected text, is not a click on the row either. */
                  onClick={
                    opens
                      ? (event) => {
                          if (wasDrag(event) || !onRowItself(event)) return
                          if (window.getSelection()?.toString()) return
                          if (!to) onRowClick(row)
                          else if (event.ctrlKey || event.metaKey || event.shiftKey) {
                            window.open(base + to, '_blank', 'noopener')
                          } else navigate(to)
                        }
                      : undefined
                  }
                  /* The middle button opens a new tab, as on a link. Its press is kept from
                     starting the browser's autoscroll, which would swallow the release. */
                  onMouseDown={
                    opens
                      ? (event) => {
                          pressAt.current = [event.clientX, event.clientY]
                          if (to && event.button === 1 && onRowItself(event)) event.preventDefault()
                        }
                      : undefined
                  }
                  onAuxClick={
                    to
                      ? (event) => {
                          if (event.button !== 1 || wasDrag(event) || !onRowItself(event)) return
                          window.open(base + to, '_blank', 'noopener')
                        }
                      : undefined
                  }
                >
                  {shown.map((column) => (
                    <td
                      key={column.key}
                      className={`data-table__td data-table__cell--${column.align ?? 'start'}${
                        isActions(column) ? ' data-table__cell--actions' : ''
                      }`}
                      /* The column's name, for the phone layout where a row is a card. */
                      data-label={typeof column.header === 'string' && !isActions(column) ? column.header : undefined}
                    >
                      {column.render ? column.render(row, start + index) : <TextCell value={cellValue(column, row)} />}
                    </td>
                  ))}
                </tr>
                )
              })}
            </tbody>
          )}
        </table>

        {/* A REFUSAL IS NOT A FAILURE. `api.js` flags every 403 as `denied`, and every screen
            that renders a list already hands its load error to this component — so telling
            the two apart HERE covers the whole panel at once instead of asking forty-odd
            screens to each remember. "Something went wrong" sends someone to retry a screen
            their role will never open; this sends them to an administrator. */}
        {!loading && error?.denied ? (
          <DeniedState description={error.message || undefined} />
        ) : !loading && error ? (
          <ErrorState title={S.loadFailed} description={error.message || undefined} />
        ) : (
          /* With a search in the box, nothing shown means nothing MATCHED — saying the
             screen's "nothing in the system" there reads as if the data were gone. */
          !loading &&
          visible.length === 0 && <EmptyState title={query.trim() ? S.noMatch : emptyMessage} />
        )}
      </div>

      <footer className="data-table__footer">
        <p className="data-table__info tabular" data-testid="table-info">
          <bdi>
            {totalRows === 0
              ? S.infoEmpty
              : S.info(start + 1, start + visible.length, totalRows)}
          </bdi>
          {!server && query.trim() && sorted.length !== rows.length && (
            <bdi className="data-table__info-filtered"> {S.infoFiltered(rows.length)}</bdi>
          )}
        </p>

        <div className="data-table__paging">
        {paginate && totalRows > PAGE_SIZES[0] && (
          <label className="data-table__length">
            <select
              value={pageSize}
              onChange={(event) => apply({ pageSize: Number(event.target.value), page: 1 })}
              aria-label={S.perPage}
              data-testid="table-length"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <bdi>{S.perPage}</bdi>
          </label>
        )}

        {/* No rows, no pages: a lone "1" between two disabled steps paged nothing. */}
        {paginate && totalRows > 0 && (
          <nav className="data-table__pagination" aria-label={S.pagination} data-testid="table-pagination">
            {/* In RTL "previous" points right: the glyphs are chosen for RTL and mirrored
                for LTR. */}
            <button
              type="button"
              className="data-table__page data-table__page--step"
              disabled={currentPage === 1}
              onClick={() => apply({ page: currentPage - 1 })}
              aria-label={S.previous}
              title={S.previous}
              data-testid="table-prev"
            >
              <ChevronRight className="ltr-flip" size={16} strokeWidth={2} />
            </button>

            {pageWindow(currentPage, pageCount).map((item, index) =>
              item === 'gap' ? (
                <span key={`gap-${index}`} className="data-table__gap">
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={`data-table__page${item === currentPage ? ' is-active' : ''}`}
                  aria-current={item === currentPage ? 'page' : undefined}
                  onClick={() => apply({ page: item })}
                  data-testid="table-page"
                >
                  {item}
                </button>
              ),
            )}

            <button
              type="button"
              className="data-table__page data-table__page--step"
              disabled={currentPage === pageCount}
              onClick={() => apply({ page: currentPage + 1 })}
              aria-label={S.next}
              title={S.next}
              data-testid="table-next"
            >
              <ChevronLeft className="ltr-flip" size={16} strokeWidth={2} />
            </button>
          </nav>
        )}
        </div>
      </footer>

      {footer && <div className="data-table__actions">{footer}</div>}
    </section>
  )
}
